import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'auth/AuthProvider';
import { PERMISSIONS } from 'auth/permissions';
import { captureRevision, setExpectedRevision } from 'core/api/revisionStore';
import { ApiError } from 'core/errors/ApiError';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import { assessmentApi } from 'features/assessment-dashboard/assessment.api';
import { ImportDialog } from 'features/workbooks/ImportDialog';
import {
  invalidateTopThreeProfileWorkbookQueries,
  saveBlobFile,
  topThreeExportBlockReason,
  topThreeImportBlockReason,
  topThreeTemplateBlockReason,
} from 'features/workbooks/workbook';
import { Button } from 'shared/components/Button';
import { useToast } from 'shared/components/Toast';

export function TopThreeProfileWorkbookCard({
  version,
  versionId,
  foundProfiles = null,
  coverageLoaded = false,
  leadingActions = null,
  statusMessages = null,
}) {
  const { t } = useI18n();
  const { hasPermission, authorizationReady } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canExport = authorizationReady && hasPermission(PERMISSIONS.topThreeProfilesExport);
  const canImport = authorizationReady && hasPermission(PERMISSIONS.topThreeProfilesImport);
  const [pending, setPending] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const importButtonRef = useRef(null);
  const inflightRef = useRef(false);

  const templateBlockReason = authorizationReady
    ? topThreeTemplateBlockReason({ versionId, version, canExport })
    : null;
  const exportBlockReason = authorizationReady
    ? topThreeExportBlockReason({
      versionId,
      version,
      canExport,
      coverageLoaded,
      actualProfiles: foundProfiles,
    })
    : null;
  const importBlockReason = authorizationReady
    ? topThreeImportBlockReason({ versionId, version, canImport })
    : null;
  const explanations = authorizationReady
    ? [...new Set([templateBlockReason, exportBlockReason, importBlockReason].filter(Boolean))]
    : [];

  const runDownload = async (key, blocked, runner) => {
    if (inflightRef.current || blocked) {
      return;
    }
    inflightRef.current = true;
    setPending(key);
    try {
      const result = await runner();
      saveBlobFile(result.blob, result.filename);
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized()) {
        return;
      }
      if (error instanceof ApiError && error.isForbidden()) {
        navigate(paths.forbidden);
        return;
      }
      toast.error(humanizeApiError(error, t) || t('workbook.errors.downloadFailed'));
    } finally {
      inflightRef.current = false;
      setPending(null);
    }
  };

  return (
    <section
      className="top-three-actions"
      id="workbook"
      data-testid="top-three-workbook-actions"
      aria-label={t('workbook.resultsTitle')}
    >
      <div className="toolbar wrap workbook-card-actions">
        {leadingActions}
        <Button
          data-testid="download-top-three-template"
          tone="secondary"
          loading={pending === 'template'}
          disabled={!authorizationReady || Boolean(templateBlockReason) || Boolean(pending)}
          onClick={() => runDownload(
            'template',
            Boolean(templateBlockReason),
            () => assessmentApi.downloadTopThreeProfileTemplate(versionId),
          )}
        >
          {t('workbook.downloadProfileTemplate')}
        </Button>
        <Button
          data-testid="export-top-three-excel"
          tone="secondary"
          loading={pending === 'export'}
          disabled={!authorizationReady || Boolean(exportBlockReason) || Boolean(pending)}
          onClick={() => runDownload(
            'export',
            Boolean(exportBlockReason),
            () => assessmentApi.exportTopThreeProfiles(versionId),
          )}
        >
          {t('workbook.exportResults')}
        </Button>
        <Button
          ref={importButtonRef}
          data-testid="import-top-three-excel"
          tone={importBlockReason ? 'secondary' : 'primary'}
          disabled={!authorizationReady || Boolean(importBlockReason) || Boolean(pending)}
          onClick={() => {
            if (!importBlockReason) {
              setImportOpen(true);
            }
          }}
        >
          {t('workbook.importResults')}
        </Button>
      </div>
      {statusMessages}
      {!authorizationReady ? (
        <p className="muted" role="status" data-testid="workbook-auth-loading">{t('auth.authorizing')}</p>
      ) : null}
      {explanations.map((key) => (
        <p key={key} className="warning-banner" role="status" data-testid={`workbook-action-reason-${key}`}>
          {t(key)}
        </p>
      ))}
      <details className="help-popover">
        <summary>{t('workbook.relationshipHelpTitle')}</summary>
        <p>{t('workbook.results.sync.canonicalKey')}</p>
        <p>{t('workbook.results.sync.mandatorySync')}</p>
        <p>{t('workbook.results.sync.personalSummaryTitle')}</p>
        <p>{t('workbook.results.sync.relationshipArrays')}</p>
        <p>{t('workbook.results.sync.catalogIds')}</p>
        <p>{t('workbook.results.sync.blankMeansUnchanged')}</p>
        <p>{t('workbook.results.sync.emptyArrayDetaches')}</p>
        <p>{t('workbook.results.sync.orderedItemRefs')}</p>
        <p>{t('workbook.results.sync.readOnlyIdentities')}</p>
        <p>{t('workbook.results.sync.historicalSnapshots')}</p>
      </details>
      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        restoreFocusRef={importButtonRef}
        kind="top-three-profiles"
        versionId={versionId}
        guidanceTitleKey="workbook.relationshipHelpTitle"
        explanations={[
          'workbook.results.sync.canonicalKey',
          'workbook.results.sync.mandatorySync',
          'workbook.results.sync.personalSummaryTitle',
          'workbook.results.sync.relationshipArrays',
          'workbook.results.sync.catalogIds',
          'workbook.results.sync.blankMeansUnchanged',
          'workbook.results.sync.emptyArrayDetaches',
          'workbook.results.sync.orderedItemRefs',
          'workbook.results.sync.readOnlyIdentities',
          'workbook.results.sync.historicalSnapshots',
        ]}
        previewRequest={(file, config) => assessmentApi.previewTopThreeProfileWorkbook(versionId, file, config)}
        commitRequest={(file, payload, config) => assessmentApi.commitTopThreeProfileWorkbook(versionId, file, payload, config)}
        downloadLatest={() => assessmentApi.exportTopThreeProfiles(versionId)}
        successAction={{ labelKey: 'nav.topThreeProfiles', path: paths.topThreeProfilesWithVersion(versionId) }}
        onSuccess={async (completed) => {
          if (completed.revision) {
            setExpectedRevision(completed.revision);
          }
          captureRevision(completed);
          await invalidateTopThreeProfileWorkbookQueries(queryClient, { versionId });
          toast.success(t('workbook.success'));
        }}
      />
    </section>
  );
}
